import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { MfaCodeInput } from '@components/Mfa/MfaCodeInput/MfaCodeInput';
import { MFA_CODE_LENGTH } from '@constants/mfa';
import styles from './styles.module.css';

/**
 * The two rendered steps of enrolling EMAIL as a factor, lifted out of `SecurityPage`.
 *
 * Purely presentational: every piece of state (password, code, cooldown, in-flight flags) still
 * lives in `SecurityPage`, which owns the flow. The split is for file size — that page sits
 * against the repo's 600-line ceiling — so this is deliberately a move, not a redesign. The SMS
 * flow, which has a step email does not, owns its own state in `SmsEnrollment`.
 */

export interface IEmailPasswordStepProps {
  password: string;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  sending: boolean;
}

export function EmailPasswordStep(props: IEmailPasswordStepProps) {
  const { t } = useTranslation();
  return (
    <form onSubmit={props.onSubmit} className={styles.subForm} noValidate>
      <div>
        <h3 className={styles.subTitle}>{t('auth.mfa.settings.emailPasswordTitle')}</h3>
        <p className={styles.subBody}>{t('auth.mfa.settings.emailPasswordBody')}</p>
      </div>
      <Field
        label={t('auth.mfa.settings.passwordLabel')}
        type="password"
        value={props.password}
        onChange={(e) => props.onPasswordChange(e.target.value)}
        hint={t('auth.mfa.enroll.passwordHint')}
        autoComplete="current-password"
        autoFocus
        required
      />
      <div className={styles.formActions}>
        <Button variant="ghost" size="md" onClick={props.onCancel} disabled={props.sending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" size="md" disabled={props.sending || props.password.length === 0}>
          {props.sending ? t('auth.mfa.settings.emailSending') : t('auth.mfa.settings.emailSendCode')}
        </Button>
      </div>
    </form>
  );
}

export interface IEmailCodeStepProps {
  code: string;
  onCodeChange: (value: string) => void;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onCancel: () => void;
  submitting: boolean;
  sending: boolean;
  cooldown: number;
}

export function EmailCodeStep(props: IEmailCodeStepProps) {
  const { t } = useTranslation();
  return (
    <form
      className={styles.subForm}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit(props.code);
      }}
    >
      <div>
        <h3 className={styles.subTitle}>{t('auth.mfa.settings.emailCodeTitle')}</h3>
        <p className={styles.subBody}>{t('auth.mfa.settings.emailCodeBody')}</p>
      </div>
      <MfaCodeInput
        label={t('auth.mfa.challenge.codeLabel')}
        value={props.code}
        onChange={props.onCodeChange}
        onComplete={(full) => props.onSubmit(full)}
        disabled={props.submitting}
        autoFocus
      />
      <div className={styles.otpRow}>
        <Button variant="secondary" size="sm" onClick={props.onResend} disabled={props.sending || props.cooldown > 0}>
          {props.cooldown > 0
            ? t('auth.mfa.settings.emailResendCooldown', { seconds: props.cooldown })
            : t('auth.mfa.settings.emailResend')}
        </Button>
        <span className={styles.sentNote} role="status">
          {t('auth.mfa.settings.emailOtpSent')}
        </span>
      </div>
      <div className={styles.formActions}>
        <Button variant="ghost" size="md" onClick={props.onCancel} disabled={props.submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={props.submitting || props.code.length !== MFA_CODE_LENGTH}
        >
          {props.submitting ? t('auth.mfa.settings.emailConfirming') : t('auth.mfa.settings.emailConfirm')}
        </Button>
      </div>
    </form>
  );
}
